import tw, { styled, css } from 'twin.macro';
import {
  variants,
  sizeDefaultStyles,
  sizeSmallStyles,
  sizeTinyStyles,
  StyledIcon as LinkStyledIcon,
} from '../Link/Link.styles';

// Shared styles for LoginLink buttons and links
const loginLinkItemStyles = css`
  ${tw`w-full`}
  ${tw`mb-0`}
  ${tw`border-0`}
  ${tw`text-body-s tracking-normal`}
  ${tw`py-[4px] px-1`}
  ${tw`rounded`}
  ${tw`gap-x-[6px]`}

  &:hover {
    ${tw`bg-ui-colours-ui1`}
  }

  &:focus {
    ${tw`bg-transparent`}
  }
`;

// Shared styles for LoginLink icons
const loginLinkIconStyles = css`
  ${tw`h-[18px] w-[18px]`}
  ${tw`mr-1`}
`;

export const StyledWrapper = styled.div<{ buttonWidthFull?: boolean }>`
  ${({ buttonWidthFull }) => buttonWidthFull && tw`w-full`}
  ${tw`relative inline-block`}
`;

export const StyledButton = styled.button<{
  variant?: string;
  size?: string;
  buttonWidthFull?: boolean;
}>`
  ${({ variant }) => css`
    ${variant === 'primary' && variants.primary}
    ${variant === 'primary_dark' && variants.primary_dark}
    ${variant === 'secondary' && variants.secondary}
    ${variant === 'secondary_dark' && variants.secondary_dark}
    ${!variant && variants.primary}
  `}
  ${({ size }) => css`
    ${size === 'large' && sizeDefaultStyles}
    ${size === 'small' && sizeSmallStyles}
    ${size === 'tiny' && sizeTinyStyles}
    ${!size && sizeDefaultStyles}
  `}
  ${({ buttonWidthFull }) => buttonWidthFull && tw`w-full`}
  &:focus {
    outline: 2px dashed #290042; // Persistent purple
    outline-offset: 4px;
  }
`;

export const StyledIcon = styled(LinkStyledIcon)`
  ${tw`h-[16px] w-[16px]`}
  ${tw`ml-0`}
`;

export const StyledDropdown = styled.div<{
  width?: 'small' | 'medium' | 'large' | null;
  isOpen?: boolean;
  isTransitioning?: boolean;
}>`
  ${({ width }) => css`
    ${width === 'small' && tw`w-[273px] max-w-[100%]`}
    ${width === 'medium' && tw`w-[333px] max-w-[100%]`}
    ${width === 'large' && tw`w-[393px] max-w-[100%]`}
    ${!width && tw`w-[273px] max-w-[100%]`}
  `}
  ${tw`fixed`}
  ${tw`left-0 top-[2px]`}
  ${tw`border-2 border-primary-p1`};
  ${tw`bg-neutrals-n1`};
  ${tw`rounded-xl`};
  ${tw`shadow-[0_4px_8px_4px_rgba(18,110,211,0.15)]`};
  ${tw`py-[22px] px-[20px]`};
  ${tw`list-none`};
  ${tw`m-0`};
  ${tw`z-20`}
  ${tw`max-h-[470px] overflow-y-auto`};

  /* Performance optimizations for transform */
  will-change: ${({ isOpen }) => (isOpen ? 'transform' : 'auto')};
  transform: translateZ(0);
  contain: layout style paint; /* Optimize paint and layout performance */

  /* Always in DOM, controlled by visibility and opacity */
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  opacity: ${({ isTransitioning }) => (isTransitioning ? 1 : 0)};

  /* Different transition speeds for opening and closing */
  transition: ${({ isTransitioning }) =>
    isTransitioning
      ? 'opacity 0.15s ease-in, visibility 0.15s ease-in'
      : 'opacity 0.08s ease-out, visibility 0.08s ease-out'};
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
`;

export const StyledDropdownItems = styled.ul`
  ${tw`flex flex-col gap-[12px]`};

  /* LoginLink style overrides for Buttons */
  li form {
    ${tw`mb-0`}
  }

  li form button,
  li > button {
    ${loginLinkItemStyles}
  }

  /* Style LoginLink icons */
  li form button span svg,
  li > button span svg {
    ${loginLinkIconStyles}
  }
  /* Style LoginLink icon container */
  div[role='none'] li button span {
    ${loginLinkIconStyles}
  }

  /* LoginLink style overrides for Links only (not NavigationDropdownLink) */
  div[role='none'] li a {
    ${loginLinkItemStyles}

    &:focus-visible {
      outline: 2px solid #126ed3; // Qube blue
      outline-offset: -1px;
    }
  }
`;

export const StyledSubheadline = styled.h4`
  ${tw`font-inter font-semi-bold`};
  ${tw`text-[14px] text-secondary-s1`}
  ${tw`leading-[18px] tracking-[2.1px]`};
  ${tw`p-1`}
  ${tw`mb-1`}
`;
